import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import { financeApi } from '../../api/financeApi';
import {
  chartColors,
  gridProps,
  axisTick,
  quietAxisProps,
  tooltipContentStyle,
  barRadiusHorizontal,
  MAX_BAR_SIZE,
  SERIES_FILL_OPACITY,
  activeBarProps,
} from './chartTheme';

interface Props {
  dateFrom: string;
  dateTo: string;
  limit?: number;
}

interface ExpenseRow {
  supplierName?: string;
  amount?: number;
  // Backend ExpenseResponse exposes a flat categoryName, not a nested category object.
  categoryName?: string;
}

interface ExpensesPage {
  content?: ExpenseRow[];
}

const UNSPECIFIED_LABEL = 'Unspecified';

const TopPayeesChart: React.FC<Props> = ({ dateFrom, dateTo, limit = 10 }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    financeApi
      .getExpenses({ page: 0, size: 1000, dateFrom, dateTo })
      .then((res) => {
        if (cancelled) return;
        const page = res.data as ExpensesPage;
        const content = Array.isArray(page?.content) ? page.content : [];
        setRows(content);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load expenses');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  const data = useMemo(() => {
    const totals = new Map<string, number>();
    rows.forEach((r) => {
      const supplier = (r.supplierName || '').trim();
      // category_id is NOT NULL at the DB level, so we don't fall back to it —
      // an "Unspecified" bucket is exactly the rows where the user never typed
      // a supplier name. The backend `unspecified=true` filter matches the same.
      const name = supplier || UNSPECIFIED_LABEL;
      totals.set(name, (totals.get(name) || 0) + (r.amount || 0));
    });
    return Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([name, total]) => ({ name, total }));
  }, [rows, limit]);

  const handleBarClick = (payload: { name?: string } | null) => {
    if (!payload?.name) return;
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (payload.name === UNSPECIFIED_LABEL) {
      params.set('unspecified', 'true');
    } else {
      params.set('supplierName', payload.name);
    }
    navigate(`/vendor/finance/expenses?${params.toString()}`);
  };

  if (loading) {
    return (
      <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
        Loading…
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.danger }}>
        {error}
      </Box>
    );
  }
  if (data.length === 0) {
    return (
      <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
        No expense payees in this range
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: '0.72rem', color: theme.gray?.[500] || '#666', mb: 1 }}>
        Top {data.length} by total spend · click a bar to view & assign
      </Typography>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 32 + 40)}>
        <BarChart layout="vertical" data={data} margin={{ top: 4, right: 24, left: 16, bottom: 4 }}>
          {/* Horizontal layout: value gridlines run vertically, so flip the shared default. */}
          <CartesianGrid {...gridProps} vertical horizontal={false} />
          <XAxis
            type="number"
            tick={axisTick}
            tickFormatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
            {...quietAxisProps}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ ...axisTick, fill: theme.text, cursor: 'pointer' }}
            onClick={(o: { value?: string }) => handleBarClick({ name: o?.value })}
            {...quietAxisProps}
          />
          <Tooltip
            formatter={(value: number) => formatCurrency(value, { showDecimals: false })}
            contentStyle={tooltipContentStyle}
          />
          <Bar
            dataKey="total"
            fill={chartColors.expense}
            fillOpacity={SERIES_FILL_OPACITY}
            radius={barRadiusHorizontal}
            maxBarSize={MAX_BAR_SIZE}
            cursor="pointer"
            onClick={(payload) => handleBarClick(payload as { name?: string })}
            {...activeBarProps}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TopPayeesChart;
